import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import PageTypePicker from "./PageTypePicker";
import "../../builder.css";

const PAGE_TYPE_LABELS = {
  context: "Contexte",
  mosaic: "Mosaïque",
  "one-choice": "Choix unique",
  score: "Score",
  command_control: "Commande",
  two_doughnuts_graphs: "Double graphe",
};

function WorkshopBuilder() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const tab = searchParams.get("tab") || "configs";
  const setTab = (t) => { setSearchParams({ tab: t }); setSearch(""); };
  const [configs, setConfigs] = useState([]);
  const [pages, setPages] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  // Créer une page depuis la bibliothèque passe par le même choix de type que
  // depuis la pile d'un atelier : c'est le type qui décide de l'écran d'après.
  const [showPageTypePicker, setShowPageTypePicker] = useState(false);

  const apiGet = async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  useEffect(() => {
    setLoading(true);
    if (tab === "configs") {
      apiGet("/get_all_game_configs")
        .then(setConfigs)
        .finally(() => setLoading(false));
    } else if (tab === "pages") {
      apiGet("/get_all_pages")
        .then(setPages)
        .finally(() => setLoading(false));
    } else {
      apiGet("/get_all_decisions")
        .then(setDecisions)
        .finally(() => setLoading(false));
    }
  }, [tab]);

  const filteredConfigs = configs.filter(
    (c) =>
      c._id.toLowerCase().includes(search.toLowerCase()) ||
      c.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredPages = pages.filter(
    (p) =>
      p._id.toLowerCase().includes(search.toLowerCase()) ||
      (p.title || "").toLowerCase().includes(search.toLowerCase())
  );
  const filteredDecisions = decisions.filter(
    (d) =>
      d._id.toLowerCase().includes(search.toLowerCase()) ||
      (d.title || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="builder-container">
      <div className="builder-header">
        <button className="builder-back-btn" onClick={() => navigate("/profile")}>
          ← Retour
        </button>
        <h1 className="builder-title">Constructeur d'ateliers</h1>
      </div>

      <div className="builder-tabs">
        {[
          { key: "configs", label: "🎮 Ateliers" },
          { key: "pages", label: "📄 Pages" },
          { key: "decisions", label: "✅ Décisions" },
        ].map((t) => (
          <button
            key={t.key}
            className={`builder-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >

            {t.label}
          </button>
        ))}
      </div>

      <div className="builder-toolbar">
        <input
          className="builder-search"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className="builder-btn-primary"
          onClick={() => {
            if (tab === "configs") navigate("/builder/config/new");
            else if (tab === "pages") setShowPageTypePicker(true);
            else navigate("/builder/decision/new", { state: { from: { path: "/builder?tab=decisions", label: "Bibliothèque" } } });
          }}
        >
          + Nouveau
        </button>
      </div>

      {loading ? (
        <div className="builder-loading">Chargement...</div>
      ) : (
        <div className="builder-list">
          {tab === "configs" &&
            filteredConfigs.map((c) => (
              <div
                key={c._id}
                className="builder-card"
                onClick={() => navigate(`/builder/config/${c._id}`)}
              >
                <div className="builder-card-title">{c.name}</div>
                <div className="builder-card-meta">
                  <span className="builder-tag">{c._id}</span>
                  {(c.tags || []).map((t) => (
                    <span key={t} className="builder-tag tag-type">{t}</span>
                  ))}
                  <span className="builder-tag">{(c.pile || []).length} pages</span>
                </div>
              </div>
            ))}

          {tab === "pages" &&
            filteredPages.map((p) => (
              <div
                key={p._id}
                className="builder-card"
                onClick={() => navigate(`/builder/page/${p._id}`, { state: { from: { path: "/builder?tab=pages", label: "Bibliothèque" } } })}
              >
                <div className="builder-card-title">{p.title || p._id}</div>
                <div className="builder-card-meta">
                  <span className="builder-tag">{p._id}</span>
                  <span className="builder-tag tag-type">
                    {PAGE_TYPE_LABELS[p.type] || p.type}
                  </span>
                </div>
              </div>
            ))}

          {tab === "decisions" &&
            filteredDecisions.map((d) => (
              <div
                key={d._id}
                className="builder-card"
                onClick={() => navigate(`/builder/decision/${d._id}`, { state: { from: { path: "/builder?tab=decisions", label: "Bibliothèque" } } })}
              >
                <div className="builder-card-title">{d.title || d._id}</div>
                <div className="builder-card-meta">
                  <span className="builder-tag">{d._id}</span>
                  {(d.tags || []).map((t) => (
                    <span key={t} className="builder-tag tag-type">{t}</span>
                  ))}
                  <span className="builder-tag">
                    {Object.keys(d.score || {}).length} impacts
                  </span>
                </div>
              </div>
            ))}

          {tab === "configs" && filteredConfigs.length === 0 && !loading && (
            <div className="builder-empty">Aucun atelier trouvé</div>
          )}
          {tab === "pages" && filteredPages.length === 0 && !loading && (
            <div className="builder-empty">Aucune page trouvée</div>
          )}
          {tab === "decisions" && filteredDecisions.length === 0 && !loading && (
            <div className="builder-empty">Aucune décision trouvée</div>
          )}
        </div>
      )}

      {showPageTypePicker && (
        <PageTypePicker
          onCancel={() => setShowPageTypePicker(false)}
          onPick={(type) => {
            setShowPageTypePicker(false);
            navigate("/builder/page/new", {
              state: { newType: type, from: { path: "/builder?tab=pages", label: "Bibliothèque" } },
            });
          }}
        />
      )}
    </div>
  );
}

export default WorkshopBuilder;
